import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import useToast, { TOAST_TYPE } from './useToast.js';

/**
 * Custom hook to handle the file logger functionality
 * @param file - the file object to fetch the log content
 * @param getLogContent function to fetch the log content
 * @returns {{isFetchingLogPartitions: boolean, fetchLatestLogPartition: fetchLatestLogPartition, startFetchingLogPartition: startFetchingLogPartition, stopFetchingLogPartitions: stopFetchingLogPartitions, resetLogPartition: resetLogPartition, partitionContent: boolean}}
 */
const useFileLogger = (file, getLogContent) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [lastByte, setLastByte] = useState(0); // This state is really important because is also used as initial point to fetch the log content
  // Since we are using setInterval function, every time a state we are using inside the
  // getLogPartition, setInterval do not rebind the new value of the state and will always
  // use the values from the initial render. Therefore, we need to use a mutable object to store
  const logRequestStateRef = useRef({}); // Mutable object to store multiple states
  const logIntervalRef = useRef(null); // To store the interval ID
  const [isFetchingLogPartitions, setIsFetching] = useState(false); // To track if fetching is active
  const [endOfFileMessage, setEndOfFileMessage] = useState(false);
  const [partitionContent, setPartitionContent] = useState(false);

  const startFetchingLogPartition = () => {
    setEndOfFileMessage(
      `\n =========== ${t('loading', { ns: 'descriptions' })} ===========`
    );
    setIsFetching(true);
  };

  const stopFetchingLogPartitions = () => {
    setEndOfFileMessage('');
    setIsFetching(false);
  };

  const fetchLatestLogPartition = () => {
    stopFetchingLogPartitions();
    setLastByte(0); // The request is going to be bytes=-${chunkSize}
    setPartitionContent(partitionContent);
    startFetchingLogPartition();
  };

  const resetLogPartition = () => {
    setPartitionContent('');
    setLastByte(0);
    setEndOfFileMessage('');
  };

  /**
   * Fetches the log partition content and updates the view modal content
   * state values such as file, lastByte, etc are accessed from the ref
   * @returns {Promise<void>}
   */
  const getLogPartition = async () => {
    const {
      file: currentFile, // Rename to currentFile to avoid conflict with the file state
      lastByte: prevLastByte,
      partitionContent: prevContent,
    } = logRequestStateRef.current; // Access the latest values

    const {
      lastByte: newLastByte,
      totalSize,
      content,
      canFetchMore,
    } = await getLogContent(currentFile, prevLastByte);

    if (!canFetchMore) {
      stopFetchingLogPartitions();
      showToast(TOAST_TYPE.INFO, t('endOfLogFile', { ns: 'notifications' }));

      setEndOfFileMessage(
        `\n =========== ${t('endOfLogFile', { ns: 'descriptions' })} ===========`
      );
    }
    if (totalSize > 0) setPartitionContent(prevContent + content);
    setLastByte(newLastByte);
  };

  // Fetch log partitions
  useEffect(() => {
    if (isFetchingLogPartitions) {
      if (lastByte === 0) getLogPartition(); // Fetch the first partition and not wait 5000ms
      logIntervalRef.current = setInterval(() => {
        getLogPartition();
      }, 5000);
    } else {
      clearInterval(logIntervalRef.current);
    }
    return () => clearInterval(logIntervalRef.current);
  }, [isFetchingLogPartitions]);

  // Update the logRequest ref whenever any state changes
  useEffect(() => {
    logRequestStateRef.current = {
      file,
      lastByte,
      partitionContent,
    };
  }, [file, lastByte, partitionContent]);

  return {
    partitionContent,
    isFetchingLogPartitions,
    endOfFileMessage,
    startFetchingLogPartition,
    stopFetchingLogPartitions,
    fetchLatestLogPartition,
    resetLogPartition,
  };
};

export default useFileLogger;
